import {
	Component,
	EventEmitter,
	HostBinding,
	Input,
	OnInit,
	Output,
	TemplateRef
}                       from '@angular/core';
import {
	SvgSizeEnum,
	SvgStateEnum,
	SvgTypeEnum
}                       from '@app/shared/components/svg/svg.model';
import { LIMIT_TYPES }  from '@appConfig/app-mode-config';
import { IKeyValueMap } from '@core/interfaces/common.interfaces';
import {
	ApplicationScopeTypes,
	ScopeActionEnum,
	ScopePermissionCheckEnum
}                       from '@models/scope.enums';
import {
	ActionDisplayModeEnum,
	ActionEnum,
	ActionIconSizeEnum,
	ActionIconTypeEnum
}                       from '@shared/components/action-item/action-item.enum';

@Component( {
	selector: 'app-action-item[displayMode][actionItem][scopes]',
	templateUrl: './action-item.component.html',
	styleUrls: [ './action-item.component.scss' ]
} )
export class ActionItemComponent implements OnInit {

	private _hasNoPermission: boolean;
	private _disabled = false;
	isDisabled = false;

	// Obligatory Inputs
	_actionItem: ActionEnum;

	get actionItem(): ActionEnum {
		return this._actionItem;
	}

	@Input() set actionItem( actionItem: ActionEnum ) {
		this._actionItem = actionItem;
		this.setDefaultProperties();
	}

	@Input() displayMode: ActionDisplayModeEnum;

	@Input() scopes: ApplicationScopeTypes | ApplicationScopeTypes[] = null; // Used to apply appAccessFlowCheck
	@Input() scopeAction: ScopeActionEnum = ScopeActionEnum.DISABLE; // Used to apply appAccessFlowCheck
	@Input() scopePermissionCheck: ScopePermissionCheckEnum; // Used to apply appAccessFlowCheck

	// Optional Inputs
	@Input() label = null;     // Used to show label beside the icon (label will switch component to dropdownItem)
	@Input() labelStyleClass = '';   // Used to style label beside the icon
	@Input() tooltip = null;     // Used to show tooltip
	@Input() tooltipHtml: TemplateRef<HTMLElement>; // Used to show html tooltip
	@Input() disableTooltip = false;     // Used to show tooltip

	@Input() set disabled( value: boolean ) {
		this._disabled = value;
		this.updateIsDisabled();
	}

	@Input() showLoader = false;    // Used to show loader

	@Input() actionIconType = ActionIconTypeEnum.StyleClass;    // Used to switch icons by svg, image or style class
	@Input() actionIconSize: ActionIconSizeEnum;

	@Input() svgFillable = true;

	@Input() svgStrokeable = true;

	@Input( 'iconNgClassMap' )    // set icon class
	set iconNgClassMap( iconNgClassMap: IKeyValueMap<boolean> ) {
		this.iconClassMap = iconNgClassMap;
		this.setDefaultProperties();
	}

	@Input( 'iconClassString' )   // set icon class
	set iconClassString( iconClass: string ) {
		this.iconClass = iconClass;
		this.setDefaultProperties();
	}

	@Output() clickEvent = new EventEmitter<null>();    // Used to switch icons by svg, image or style class

	@HostBinding( 'class' ) hostClass: string;

	@Input() set baseClass( baseClass: string ) {
		this.hostClass = this.hostClass = baseClass ? baseClass : null;
	}

	// Calculated Properties
	limitType;

	iconClass = '';

	actionIconSizeEnum = ActionIconSizeEnum;
	showSmallLoaderIcon: boolean;

	iconClassMap: IKeyValueMap<boolean>;

	svgType: SvgTypeEnum;

	svgStateEnum = SvgStateEnum;

	svgSizeEnum = SvgSizeEnum;

	actionDisplayModeEnum = ActionDisplayModeEnum;

	imageSrcPath: string;

	// Helpers or Enums
	imageSrcBasePath = './assets/img/';

	actionIconTypeEnum = ActionIconTypeEnum;

	isHovered = false;

	constructor() { }

	// TODO provide missing svg and images for existing ActionTypes
	private setDefaultProperties() {

		// TODO provide some fallback svg/image
		// no icon fallback
		// this.svgType = SvgTypeEnum;
		// this.imageSrcPath = this.imageSrcBasePath + '';

		let iconClassByType = this.iconClass ? this.iconClass : null;
		this.svgType = SvgTypeEnum.NO_ICON;

		if ( !this.iconClass ) {
			switch ( this.actionItem ) {
				case ActionEnum.ACKNOWLEDGE:
					this.svgType = SvgTypeEnum.ACKNOWLEDGE;
					break;
				case ActionEnum.ASSIGN:
					this.svgType = SvgTypeEnum.ASSIGN;
					this.imageSrcPath = this.imageSrcBasePath + '';
					break;
				case ActionEnum.ACTIVATE:
					this.svgType = SvgTypeEnum.UNLOCKED;
					this.imageSrcPath = this.imageSrcBasePath + '';
					break;
				case ActionEnum.COMPLAIN:
					this.svgType = SvgTypeEnum.COMPLAIN;
					this.imageSrcPath = this.imageSrcBasePath + '';
					break;
				case ActionEnum.CONFIGURE:
					iconClassByType = 'icon-settings';
					break;
				case ActionEnum.COPY:
					iconClassByType = 'icon-docs';
					break;
				case ActionEnum.CREATE:
					this.limitType = LIMIT_TYPES.CREATE_ENTITY;
					iconClassByType = 'icon-plus';
					break;
				case ActionEnum.DECOMMISSION:
					this.svgType = SvgTypeEnum.DECOMMISSION;
					break;
				case ActionEnum.DEFAULT:
					this.svgType = SvgTypeEnum.DEFAULT;
					break;
				case ActionEnum.DELETE:
					iconClassByType = 'icon-trash';
					break;
				case ActionEnum.DETAILS:
					this.svgType = SvgTypeEnum.DETAILS;
					iconClassByType = 'icon-magnifier';
					break;
				case ActionEnum.DISABLE:
					this.svgType = SvgTypeEnum.LOCKED;
					this.imageSrcPath = this.imageSrcBasePath + '';
					break;
				case ActionEnum.DOWNLOAD:
					this.svgType = SvgTypeEnum.DOWNLOAD;
					this.imageSrcPath = this.imageSrcBasePath + '';
					break;
				case ActionEnum.EDIT:
					iconClassByType = 'icon-pencil';
					break;
				case ActionEnum.TAXATION_REPORTING_CREATE:
					this.svgType = SvgTypeEnum.AUTHORITIES_BADGE;
					break;
				case ActionEnum.TAXATION_REPORTING_SUBMIT:
					this.svgType = SvgTypeEnum.ELSTER_SUBMIT;
					break;
				case ActionEnum.EXTEND:
					iconClassByType = 'icon-hourglass';
					break;
				case ActionEnum.EXPORT:
					iconClassByType = 'icon-cloud-download';
					break;
				case ActionEnum.FCC_DATACENTER:
					this.svgType = SvgTypeEnum.DATACENTER;
					this.imageSrcPath = this.imageSrcBasePath + '';
					break;
				case ActionEnum.FINAL_EXPORT_CONFIRM:
					this.svgType = SvgTypeEnum.CHECKERED_FLAG;
					this.imageSrcPath = this.imageSrcBasePath + '';
					break;
				case ActionEnum.IMPORT:
					this.limitType = LIMIT_TYPES.IMPORT_ENTITY;
					iconClassByType = 'icon-cloud-upload';
					break;
				case ActionEnum.KEYWORD_CHANGE:
					this.svgType = SvgTypeEnum.KEYWORD;
					this.imageSrcPath = this.imageSrcBasePath + '';
					break;
				case ActionEnum.LINK:
					iconClassByType = 'icon-link';
					break;
				case ActionEnum.MANAGE_TENANT:
					this.svgType = SvgTypeEnum.MANAGE_TENANT;
					iconClassByType = 'icon-social-dropbox';
					break;
				case ActionEnum.MANIFEST_ASSIGN:
					this.svgType = SvgTypeEnum.MANIFEST_ASSIGN;
					break;
				case ActionEnum.MANUAL_EXPORT:
					this.svgType = SvgTypeEnum.MANUAL_EXPORT;
					this.imageSrcPath = this.imageSrcBasePath + '';
					break;
				case ActionEnum.PASSWORD_CHANGE:
					iconClassByType = 'icon-eye';
					break;
				case ActionEnum.PASSWORD_RESET:
					iconClassByType = 'icon-eye';
					break;
				case ActionEnum.PATCH:
					this.svgType = SvgTypeEnum.PATCH;
					break;
				case ActionEnum.PRINT:
					iconClassByType = 'icon-printer';
					break;
				case ActionEnum.REACTIVATE:
					this.svgType = SvgTypeEnum.REACTIVATE;
					this.imageSrcPath = this.imageSrcBasePath + '';
					break;
				case ActionEnum.REFRESH:
					this.svgType = SvgTypeEnum.RELOAD;
					break;
				case ActionEnum.REINITIALIZE:
					this.svgType = SvgTypeEnum.REACTIVATE;
					this.imageSrcPath = this.imageSrcBasePath + '';
					break;
				case ActionEnum.REQUEST_TSS:
					iconClassByType = 'icon-key';
					break;
				case ActionEnum.RESET:
					this.svgType = SvgTypeEnum.RESET;
					this.imageSrcPath = this.imageSrcBasePath + '';
					break;
				case ActionEnum.RETURN:
					this.svgType = SvgTypeEnum.RETURN;
					this.imageSrcPath = this.imageSrcBasePath + '';
					break;
				case ActionEnum.SUSPEND:
					this.svgType = SvgTypeEnum.SUSPENDED;
					this.imageSrcPath = this.imageSrcBasePath + '';
					break;
				case ActionEnum.USAGE:
					this.svgType = SvgTypeEnum.USAGE;
					iconClassByType = 'icon-bar-chart';
					break;
				default:
					iconClassByType = 'icon-question'; // fallback
			}
		}

		this.iconClassMap = { ...this.iconClassMap };
		if ( !this.iconClassMap.hasOwnProperty( iconClassByType ) ) {
			this.iconClassMap = { ...this.iconClassMap, [ iconClassByType ]: true };
		}
	}

	updateIsDisabled(): void {
		this.isDisabled = this._hasNoPermission
											? true
											: this._disabled;
	}

	updateAccessCheckResult( accessCheckResult: string ) {
		this._hasNoPermission = true;

		switch ( accessCheckResult ) {
			case 'hide':
				break;
			case 'disable':
				const timeout = setTimeout( () => {
					clearTimeout( timeout );
					this.updateIsDisabled();
				}, 0 );
				break;
		}
	}

	onClick() {
		this.clickEvent.emit( null );
	}

	ngOnInit(): void {
		this.setDefaultProperties();

		// Used to set icon size
		switch ( this.displayMode ) {
			case ActionDisplayModeEnum.DropdownItem:
				switch ( this.actionIconType ) {
					case ActionIconTypeEnum.Image:
					case ActionIconTypeEnum.SVG:
						this.showSmallLoaderIcon = true;
						this.actionIconSize = this.actionIconSize ?? ActionIconSizeEnum.Small;
						break;

					case ActionIconTypeEnum.StyleClass:
						this.showSmallLoaderIcon = true;
						this.actionIconSize = this.actionIconSize ?? ActionIconSizeEnum.Medium;
						break;
				}
				break;

			case ActionDisplayModeEnum.Inline:
				switch ( this.actionIconType ) {
					case ActionIconTypeEnum.Image:
					case ActionIconTypeEnum.SVG:
					case ActionIconTypeEnum.StyleClass:
						this.showSmallLoaderIcon = true;
						this.actionIconSize = this.actionIconSize ?? ActionIconSizeEnum.Medium;
						break;
				}
				break;

			case ActionDisplayModeEnum.Standalone:
				switch ( this.actionIconType ) {
					case ActionIconTypeEnum.Image:
					case ActionIconTypeEnum.SVG:
						this.showSmallLoaderIcon = true;
						this.actionIconSize = this.actionIconSize ?? ActionIconSizeEnum.Medium;
						break;

					case ActionIconTypeEnum.StyleClass:
						this.showSmallLoaderIcon = false;
						this.actionIconSize = this.actionIconSize ?? ActionIconSizeEnum.Large;
						break;
				}
				break;
		}
	}

	getSVGSize(): SvgSizeEnum {
		switch ( this.actionIconSize ) {
			case ActionIconSizeEnum.Large:
				return SvgSizeEnum.LARGE;

			case ActionIconSizeEnum.Medium:
				return SvgSizeEnum.MEDIUM;

			case ActionIconSizeEnum.Small:
				return SvgSizeEnum.SMALL;

			default:
				return SvgSizeEnum.MEDIUM;
		}
	}

	getFontIconSize(): string {
		switch ( this.actionIconSize ) {
			case ActionIconSizeEnum.Large:
				return 'fs-xl';

			case ActionIconSizeEnum.Medium:
				return 'fs-md';

			case ActionIconSizeEnum.Small:
				return 'fs-s';

			default:
				return 'fs-md';
		}
	}
}
